import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/lib/auth";
import { Trash } from "@phosphor-icons/react";

const TABS = ["Comics", "Merch", "Blog", "Videos", "Launch", "Subscribers"];

export default function Admin() {
  const [tab, setTab] = useState("Comics");
  const [items, setItems] = useState({ comics: [], merch: [], blog: [], videos: [] });
  const [subs, setSubs] = useState({ count: 0, subscribers: [] });
  const [launch, setLaunch] = useState(null);
  const { user, logout } = useAuth();

  const refresh = useCallback(async () => {
    const [c, m, b, v, n, l] = await Promise.all([
      api.get("/comics"), api.get("/merch"), api.get("/blog"), api.get("/videos"), api.get("/newsletter"), api.get("/launch"),
    ]);
    setItems({ comics: c.data, merch: m.data, blog: b.data, videos: v.data });
    setSubs(n.data);
    setLaunch(l.data);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      await refresh();
      if (!alive) return;
    })();
    return () => { alive = false; };
  }, [refresh]);

  const counts = {
    Comics: items.comics.length,
    Merch: items.merch.length,
    Blog: items.blog.length,
    Videos: items.videos.length,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="admin-page">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <div className="section-eyebrow">Control Room</div>
          <h1 className="font-display text-5xl lg:text-6xl uppercase mt-3">Admin</h1>
          {user && <p className="text-dim mt-2 text-sm">Signed in as <span className="text-white">{user.email}</span></p>}
        </div>
        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Sign Out</button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 font-display tracking-widest text-xs uppercase border ${tab === t ? "bg-[#FF3B30] text-black border-[#FF3B30]" : "border-white/20 hover:border-[#FF3B30]"}`}>
            {t} {counts[t] != null && <span className="opacity-60 ml-1">({counts[t]})</span>}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-8">
        {tab === "Comics" && (
          <>
            <ComicForm onDone={refresh} />
            <ContentList kind="comic" endpoint="/comics" items={items.comics} onDone={refresh}
                         getLabel={(i) => `${i.title} — ${i.issue}`} getImage={(i) => i.cover_image} />
          </>
        )}
        {tab === "Merch" && (
          <>
            <MerchForm onDone={refresh} />
            <ContentList kind="merch" endpoint="/merch" items={items.merch} onDone={refresh}
                         getLabel={(i) => i.name} getImage={(i) => i.image} />
          </>
        )}
        {tab === "Blog" && (
          <>
            <BlogForm onDone={refresh} />
            <ContentList kind="blog post" endpoint="/blog" items={items.blog} onDone={refresh}
                         getLabel={(i) => i.title} getImage={(i) => i.cover_image} />
          </>
        )}
        {tab === "Videos" && (
          <>
            <VideoForm onDone={refresh} />
            <ContentList kind="video" endpoint="/videos" items={items.videos} onDone={refresh}
                         getLabel={(i) => i.title} getImage={(i) => i.thumbnail} />
          </>
        )}
        {tab === "Launch" && <LaunchForm key={launch?.target_date || "loading"} launch={launch} onDone={refresh} />}
        {tab === "Subscribers" && <SubsList subs={subs} />}
      </div>
    </div>
  );
}

function ContentList({ kind, endpoint, items, onDone, getLabel, getImage }) {
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (item) => {
    const label = getLabel(item);
    if (!window.confirm(`Delete this ${kind}?\n\n"${label}"\n\nThis cannot be undone.`)) return;
    setDeletingId(item.id);
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} deleted`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to delete ${kind}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="scu-card p-6" data-testid={`admin-${kind.replace(/\s+/g, "-")}-list`}>
      <div className="font-display text-2xl uppercase mb-4">Existing {kind.charAt(0).toUpperCase() + kind.slice(1)}s ({items.length})</div>
      {items.length === 0 ? (
        <div className="text-dim text-sm">None yet. Add one above.</div>
      ) : (
        <ul className="divide-y divide-white/10">
          {items.map((item) => (
            <li key={item.id} className="py-3 flex items-center gap-4" data-testid={`admin-item-${item.id}`}>
              {getImage(item) && (
                <img
                  src={getImage(item)}
                  alt=""
                  className="w-14 h-14 object-cover border border-white/10 flex-shrink-0"
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase text-sm truncate">{getLabel(item)}</div>
                <div className="text-dim text-xs truncate">{getImage(item) || "no image"}</div>
              </div>
              <button
                onClick={() => handleDelete(item)}
                disabled={deletingId === item.id}
                className="px-3 py-2 border border-red-900/40 text-red-400 hover:bg-red-950/40 hover:border-[#FF3B30] hover:text-[#FF3B30] inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest transition-colors disabled:opacity-50"
                data-testid={`admin-delete-${item.id}`}
              >
                <Trash size={14} weight="bold" />
                {deletingId === item.id ? "Deleting..." : "Delete"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-dim">{label}</span>
      <input {...props} className="mt-1 w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none" />
    </label>
  );
}
function TextArea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-dim">{label}</span>
      <textarea {...props} className="mt-1 w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none min-h-[100px]" />
    </label>
  );
}

function ComicForm({ onDone }) {
  const [f, setF] = useState({ title: "", issue: "", cover_image: "", synopsis: "", release_date: "", price: 7.99, featured: false, pages: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.cover_image) return toast.error("Cover image required");
    const pages = f.pages.split("\n").map((s) => s.trim()).filter(Boolean);
    await api.post("/comics", { ...f, price: Number(f.price), pages });
    toast.success("Comic added");
    setF({ title: "", issue: "", cover_image: "", synopsis: "", release_date: "", price: 7.99, featured: false, pages: "" });
    onDone();
  };
  return (
    <form onSubmit={submit} className="scu-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-comic-form">
      <Field label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="admin-comic-title" />
      <Field label="Issue" required value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })} data-testid="admin-comic-issue" />
      <div className="md:col-span-2">
        <FileUploader label="Cover Image" value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} accept="image/*" testid="admin-comic-cover" />
      </div>
      <Field label="Release Date" required value={f.release_date} onChange={(e) => setF({ ...f, release_date: e.target.value })} placeholder="2026-10-06" data-testid="admin-comic-release" />
      <Field label="Price ($)" type="number" step="0.01" required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="admin-comic-price" />
      <label className="flex items-center gap-2 mt-6">
        <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="admin-comic-featured" />
        <span className="text-xs uppercase tracking-widest">Featured (Flagship)</span>
      </label>
      <div className="md:col-span-2">
        <TextArea label="Synopsis" required value={f.synopsis} onChange={(e) => setF({ ...f, synopsis: e.target.value })} data-testid="admin-comic-synopsis" />
      </div>
      <div className="md:col-span-2">
        <TextArea
          label="Preview Pages (one image URL per line — leave blank if no preview)"
          value={f.pages}
          onChange={(e) => setF({ ...f, pages: e.target.value })}
          placeholder={"https://...page1.jpg\nhttps://...page2.jpg"}
          data-testid="admin-comic-pages"
        />
      </div>
      <div className="md:col-span-2"><button className="btn-red" data-testid="admin-comic-submit">Add Comic</button></div>
    </form>
  );
}

function MerchForm({ onDone }) {
  const [f, setF] = useState({ name: "", description: "", image: "", price: 29.99, category: "apparel", stock: 100 });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Image required");
    await api.post("/merch", { ...f, price: Number(f.price), stock: Number(f.stock) });
    toast.success("Merch added");
    setF({ name: "", description: "", image: "", price: 29.99, category: "apparel", stock: 100 });
    onDone();
  };
  return (
    <form onSubmit={submit} className="scu-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-merch-form">
      <Field label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Field label="Category" required value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
      <div className="md:col-span-2">
        <FileUploader label="Product Image" value={f.image} onChange={(v) => setF({ ...f, image: v })} accept="image/*" testid="admin-merch-image" />
      </div>
      <Field label="Price ($)" type="number" step="0.01" required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
      <Field label="Stock" type="number" required value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} />
      <div className="md:col-span-2"><TextArea label="Description" required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="md:col-span-2"><button className="btn-red" data-testid="admin-merch-submit">Add Merch</button></div>
    </form>
  );
}

function BlogForm({ onDone }) {
  const [f, setF] = useState({ title: "", excerpt: "", body: "", cover_image: "", tags: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.cover_image) return toast.error("Cover image required");
    await api.post("/blog", { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
    toast.success("Post published");
    setF({ title: "", excerpt: "", body: "", cover_image: "", tags: "" });
    onDone();
  };
  return (
    <form onSubmit={submit} className="scu-card p-6 grid grid-cols-1 gap-4" data-testid="admin-blog-form">
      <Field label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <FileUploader label="Cover Image" value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} accept="image/*" testid="admin-blog-cover" />
      <Field label="Tags (comma separated)" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} />
      <TextArea label="Excerpt" required value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} />
      <TextArea label="Body" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      <button className="btn-red w-fit" data-testid="admin-blog-submit">Publish</button>
    </form>
  );
}

function VideoForm({ onDone }) {
  const [f, setF] = useState({ title: "", description: "", source: "youtube", url: "", thumbnail: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.url || !f.thumbnail) return toast.error("URL and thumbnail required");
    await api.post("/videos", f);
    toast.success("Video added");
    setF({ title: "", description: "", source: "youtube", url: "", thumbnail: "" });
    onDone();
  };
  return (
    <form onSubmit={submit} className="scu-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-video-form">
      <Field label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-dim">Source</span>
        <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value, url: "" })}
                className="mt-1 w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none">
          <option value="youtube">YouTube (paste URL)</option>
          <option value="upload">Upload .mp4 / .webm</option>
        </select>
      </label>
      {f.source === "youtube" ? (
        <div className="md:col-span-2">
          <Field label="YouTube URL (watch or embed)" required value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
        </div>
      ) : (
        <div className="md:col-span-2">
          <FileUploader label="Video File" value={f.url} onChange={(v) => setF({ ...f, url: v })} accept="video/*" testid="admin-video-file" />
        </div>
      )}
      <div className="md:col-span-2">
        <FileUploader label="Thumbnail Image" value={f.thumbnail} onChange={(v) => setF({ ...f, thumbnail: v })} accept="image/*" testid="admin-video-thumb" />
      </div>
      <div className="md:col-span-2"><TextArea label="Description" required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="md:col-span-2"><button className="btn-red" data-testid="admin-video-submit">Add Video</button></div>
    </form>
  );
}

function SubsList({ subs }) {
  return (
    <div className="scu-card p-6" data-testid="admin-subs">
      <div className="flex items-center justify-between">
        <div className="font-display text-2xl uppercase">Subscribers ({subs.count})</div>
        <a
          href={`data:text/csv;charset=utf-8,${encodeURIComponent("email,name,joined\n" + (subs.subscribers || []).map((s) => `${s.email},${s.name || ""},${s.created_at}`).join("\n"))}`}
          download="scu_subscribers.csv"
          className="btn-ghost text-xs"
          data-testid="admin-export-csv"
        >
          Export CSV
        </a>
      </div>
      <div className="mt-4 divide-y divide-white/10">
        {subs.subscribers?.map((s) => (
          <div key={s.id} className="py-2 flex justify-between text-sm">
            <span>{s.email}</span>
            <span className="text-dim">{new Date(s.created_at).toLocaleDateString()}</span>
          </div>
        ))}
        {subs.count === 0 && <div className="text-dim text-sm">No subscribers yet.</div>}
      </div>
    </div>
  );
}

function LaunchForm({ launch, onDone }) {
  const [f, setF] = useState(() => ({
    target_date: (launch?.target_date || "").slice(0, 16),
    signed_total: launch?.signed_total ?? 100,
    signed_sold: launch?.signed_sold ?? 0,
  }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      target_date: f.target_date ? new Date(f.target_date).toISOString() : null,
      signed_total: Number(f.signed_total),
      signed_sold: Number(f.signed_sold),
    };
    await api.patch("/launch", payload);
    toast.success("Launch settings updated");
    onDone();
  };

  const left = Math.max(0, Number(f.signed_total) - Number(f.signed_sold));

  return (
    <form onSubmit={submit} className="scu-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-launch-form">
      <div className="md:col-span-2">
        <div className="font-display text-2xl uppercase">Pre-Launch Settings</div>
        <p className="text-dim text-sm mt-1">Drives the home-page countdown and the signed-variant stock counter.</p>
      </div>
      <label className="block md:col-span-2">
        <span className="text-xs uppercase tracking-widest text-dim">Target Date (local)</span>
        <input
          type="datetime-local" required value={f.target_date}
          onChange={(e) => setF({ ...f, target_date: e.target.value })}
          className="mt-1 w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none"
          data-testid="admin-launch-date"
        />
      </label>
      <Field label="Signed Variants Total" type="number" min="0" required value={f.signed_total}
             onChange={(e) => setF({ ...f, signed_total: e.target.value })} data-testid="admin-launch-total" />
      <Field label="Signed Variants Sold" type="number" min="0" required value={f.signed_sold}
             onChange={(e) => setF({ ...f, signed_sold: e.target.value })} data-testid="admin-launch-sold" />
      <div className="md:col-span-2 flex items-center justify-between border-t border-white/10 pt-4">
        <div className="text-xs uppercase tracking-widest text-dim">Remaining</div>
        <div className="font-display text-3xl text-[#FFD700]" data-testid="admin-launch-left">{left}</div>
      </div>
      <div className="md:col-span-2"><button className="btn-red" data-testid="admin-launch-submit">Save Launch Settings</button></div>
    </form>
  );
}

